/**
 * Hot & Cold Stats API
 *
 * Fetches the dynamic list of hot/cold game keys + RTP values used to populate
 * the merged HOT_AND_COLD section. The main settings response delivers the
 * source HOT_GAMES / COLD_GAMES sections with empty `games` arrays; this
 * endpoint is the source of truth for which games (and their RTPs) appear.
 */

import type { IHotColdStatsResponse } from '../../../features/hotCold';

const getHotColdStatsUrl = (host: string): string => `${host}/api/history/statistics/hot-cold-games`;

// Mirrors the dev-mode detection in settings.api.ts so localhost / example.com
// hosts don't fire real network requests.
const isDevelopmentMode = (apiHost: string): boolean => {
  return !apiHost ||
         apiHost.includes('example.com') ||
         apiHost.includes('localhost') ||
         apiHost === '';
};

// Empty response used in dev mode and as a graceful fallback on error.
// Returning empty arrays naturally trips the minLimit gate and removes the
// section, matching today's no-games behavior.
const EMPTY_RESPONSE: IHotColdStatsResponse = {
  hotGames: [],
  coldGames: [],
};

/**
 * Fetch hot/cold stats. Returns an empty response on dev mode, network error,
 * or malformed payloads — the consumer treats empty as "section absent".
 */
export const fetchHotColdStats = async (apiHost: string): Promise<IHotColdStatsResponse> => {
  if (isDevelopmentMode(apiHost)) {
    // Dev mode: short-circuit to empty. The mock settings response already
    // exercises the rest of the merged-section code path via supportedGames;
    // returning an empty list here just hides the section locally until the
    // real endpoint exists or a mock is wired in.
    return EMPTY_RESPONSE;
  }

  try {
    const response = await fetch(getHotColdStatsUrl(apiHost));

    if (!response.ok) {
      console.warn(`[HotColdAPI] Non-OK response (${response.status}) — section will be hidden`);
      return EMPTY_RESPONSE;
    }

    const data = (await response.json()) as Partial<IHotColdStatsResponse>;

    return {
      hotGames: Array.isArray(data?.hotGames) ? data.hotGames : [],
      coldGames: Array.isArray(data?.coldGames) ? data.coldGames : [],
    };
  } catch (error) {
    console.warn('[HotColdAPI] Failed to fetch hot/cold stats — section will be hidden:', error);
    return EMPTY_RESPONSE;
  }
};
